import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { LegalPage, P, UL } from "@/components/marketing/LegalPage";

export const Route = createFileRoute("/legal/kyc-global")({
  head: () => ({
    meta: [
      { title: "KYC, KYB & Verification (Global) — banana.cards" },
      { name: "description", content: "Know Your Customer, Know Your Business, beneficial ownership and verification requirements for Organisers using Banana Cards globally." },
      { property: "og:title", content: "KYC, KYB & Verification (Global) — banana.cards" },
      { property: "og:description", content: "KYC, KYB, beneficial ownership and verification requirements for global Organisers." },
    ],
    links: alternateLinkList("/legal/kyc-global", "en"),
  }),
  component: KycGlobalPage,
});

export function KycGlobalPage() {
  return (
    <LegalPage
      eyebrow="Legal · Verification"
      title="KYC, KYB &"
      italic="Verification."
      updated="January 2026"
      intro="The Organiser agrees that Six Studio Creative FZCO, trading through the Banana Cards platform, may carry out Know Your Customer (KYC), Know Your Business (KYB), customer due diligence, beneficial ownership verification, sanctions screening, payment risk checks, fraud checks, tax verification, and other compliance checks before approving an Organiser account, publishing an event, enabling ticket sales, enabling exhibitor payments, processing refunds, or releasing payouts."
      related={[
        { label: "KYC — Vietnam", to: "/legal/kyc-vietnam" },
        { label: "AML — Global", to: "/legal/aml-global" },
        { label: "Ticketing Terms (Global)", to: "/legal/ticketing-global" },
      ]}
      sections={[
        {
          id: "requirement",
          title: "1. Verification Requirement",
          body: (
            <>
              <P>Banana Cards may require the Organiser to complete verification before accessing some or all ticketing, exhibitor management, payment collection, payout, invoice coordination, or reporting functions. Verification may be required:</P>
              <UL items={[
                "before the Organiser account is approved",
                "before an event is published",
                "before paid ticket sales are enabled",
                "before exhibitor payments are enabled",
                "before a payout is released",
                "when ticket sales, exhibitor payments, refunds, or chargebacks exceed risk thresholds",
                "when the Organiser changes bank account details, legal details, ownership details, event details, or payout instructions",
                "when Banana Cards, its payment providers, banks, regulators, authorities, or compliance partners require additional checks",
              ]} />
              <P>Banana Cards may apply different levels of verification depending on the Organiser's country, legal form, event type, transaction volume, payout amount, refund activity, chargeback risk, payment method, and overall risk profile.</P>
            </>
          ),
        },
        {
          id: "information",
          title: "2. Information and Documents Required",
          body: (
            <>
              <P>Banana Cards may request information and documents including, without limitation:</P>
              <UL items={[
                "legal name and trading name",
                "company registration documents, certificate of incorporation, trade licence, commercial licence, or equivalent business registration",
                "registered address and operating address",
                "tax registration number, VAT registration number, GST registration number, TRN, EIN, company number, or equivalent tax identifier",
                "identity documents for directors, shareholders, beneficial owners, authorised representatives, account administrators, or payout approvers",
                "proof of authority for the person creating or managing the Organiser account",
                "ownership and control structure",
                "ultimate beneficial owner information",
                "bank account details and proof that the bank account belongs to the Organiser",
                "event name, description, location, dates, venue details, expected capacity, and commercial purpose",
                "ticket categories, exhibitor packages, sponsorship packages, booth packages, pricing, refund policy, and expected transaction volumes",
                "contracts, invoices, booking confirmations, venue agreements, sponsorship agreements, exhibitor agreements, or other supporting documents",
                "source of funds, source of wealth, or commercial justification where required for higher-risk events or transactions",
                "any other information reasonably required for KYC, KYB, tax, payout, fraud prevention, sanctions, AML, payment risk, or legal compliance purposes",
              ]} />
              <P>The Organiser must provide information that is complete, accurate, current, and not misleading.</P>
            </>
          ),
        },
        {
          id: "beneficial-ownership",
          title: "3. Beneficial Ownership and Control",
          body: (
            <>
              <P>Where the Organiser is a company, partnership, association, foundation, trust, club, non-profit organisation, government-linked entity, or other legal arrangement, Banana Cards may require information about the persons who ultimately own, control, manage, or benefit from the Organiser. This may include:</P>
              <UL items={[
                "shareholders or members",
                "ultimate beneficial owners",
                "directors, managers, trustees, partners, or controllers",
                "authorised signatories",
                "persons authorised to create events, collect payments, request payouts, or change bank details",
                "persons or entities receiving economic benefit from ticket revenue, exhibitor revenue, sponsorship revenue, booth revenue, or other event-related payments",
              ]} />
              <P>The Organiser must promptly notify Banana Cards of any material change to its ownership, control, management, authorised representatives, beneficial owners, bank account, tax status, or payout details.</P>
            </>
          ),
        },
        {
          id: "authority",
          title: "4. Authority to Act",
          body: (
            <>
              <P>The Organiser represents and warrants that any person creating an account, publishing an event, configuring ticket sales, collecting exhibitor payments, requesting payouts, changing bank details, uploading documents, or accepting these Terms has authority to act on behalf of the Organiser.</P>
              <P>Banana Cards may request evidence of authority, including board approvals, authorisation letters, powers of attorney, employment confirmation, account administrator approval, or other documentation. Banana Cards may suspend account access, event publishing, payment collection, or payouts if it cannot verify that the person acting on behalf of the Organiser has proper authority.</P>
            </>
          ),
        },
        {
          id: "sanctions-screening",
          title: "5. Sanctions, Watchlist, and Risk Screening",
          body: (
            <>
              <P>Banana Cards may screen the Organiser, its beneficial owners, directors, authorised representatives, payees, Customers, Exhibitors, sponsors, event partners, and relevant counterparties against sanctions lists, targeted financial sanctions lists, watchlists, politically exposed person databases, adverse media databases, payment risk databases, fraud databases, and other compliance sources.</P>
              <P>Screening may include UAE lists, United Nations lists, and other applicable sanctions, restricted-party, or watchlist sources depending on the countries, currencies, payment providers, and counterparties involved.</P>
              <P>Banana Cards may reject, suspend, freeze, delay, reverse, or withhold account access, event publishing, payment collection, refunds, or payouts where required or reasonably considered necessary for sanctions, KYC, AML/CFT, fraud prevention, payment risk, legal, or regulatory reasons.</P>
            </>
          ),
        },
        {
          id: "edd",
          title: "6. Enhanced Due Diligence",
          body: (
            <>
              <P>Banana Cards may require enhanced due diligence where the Organiser, event, transaction pattern, country, payment method, customer type, exhibitor profile, ownership structure, payout destination, or other circumstances present a higher risk. Enhanced due diligence may include:</P>
              <UL items={[
                "additional identity or business verification",
                "verification of source of funds or source of wealth",
                "review of event contracts, venue agreements, exhibitor agreements, or sponsor agreements",
                "review of pricing, commercial rationale, expected attendance, and expected revenue",
                "verification of beneficial ownership and control",
                "review of bank account ownership and payout destination",
                "review of refund, chargeback, and transaction behaviour",
                "additional sanctions, politically exposed person, adverse media, or fraud screening",
                "manual approval before ticket sales, exhibitor payments, or payouts are enabled",
              ]} />
              <P>Banana Cards may decline to provide services or release payouts if enhanced due diligence is not completed to Banana Cards' reasonable satisfaction.</P>
            </>
          ),
        },
        {
          id: "ongoing",
          title: "7. Ongoing Monitoring and Updates",
          body: (
            <>
              <P>KYC and KYB checks are not limited to account opening. Banana Cards may review and update verification information on an ongoing basis. The Organiser must promptly provide updated information if:</P>
              <UL items={[
                "its legal name changes",
                "its trading name changes",
                "its registered address changes",
                "its ownership or beneficial ownership changes",
                "its directors, authorised representatives, or account administrators change",
                "its tax registration details change",
                "its bank account or payout details change",
                "its event activity changes materially",
                "Banana Cards requests updated documents or information",
              ]} />
              <P>Banana Cards may suspend or limit services if information becomes outdated, incomplete, inconsistent, unverifiable, misleading, or high-risk.</P>
            </>
          ),
        },
        {
          id: "bank-account",
          title: "8. Bank Account and Payout Verification",
          body: (
            <>
              <P>Banana Cards may require the Organiser to prove that the nominated payout bank account is held in the Organiser's legal name or is otherwise properly authorised and acceptable to Banana Cards. Banana Cards may reject payout requests to:</P>
              <UL items={[
                "personal bank accounts where the Organiser is a company",
                "third-party bank accounts",
                "accounts in a different country from the Organiser without reasonable justification",
                "accounts that cannot be verified",
                "accounts associated with fraud, sanctions, disputes, chargebacks, or high-risk activity",
                "accounts that do not match the Organiser's verified legal details",
              ]} />
              <P>Banana Cards may require additional verification before accepting a new bank account or processing a payout after a bank account change.</P>
            </>
          ),
        },
        {
          id: "failure",
          title: "9. Failure to Complete Verification",
          body: (
            <>
              <P>If the Organiser fails to complete KYC, KYB, beneficial ownership, bank account, tax, sanctions, or other verification requirements, Banana Cards may:</P>
              <UL items={[
                "refuse to approve the Organiser account",
                "refuse to publish or continue publishing an event",
                "disable paid ticketing",
                "disable exhibitor payment collection",
                "disable payout requests",
                "delay, withhold, suspend, or refuse payouts",
                "suspend or restrict access to event management, ticketing, exhibitor management, invoice coordination, check-in, reporting, or other platform functions",
                "cancel or reverse transactions where required by law, payment provider rules, or risk controls",
                "terminate the Organiser's access to Banana Cards",
              ]} />
              <P>Banana Cards is not liable for any loss, delay, claim, cost, or damage arising from the Organiser's failure to complete verification or provide required information.</P>
            </>
          ),
        },
        {
          id: "accuracy",
          title: "10. Accuracy and Misrepresentation",
          body: (
            <>
              <P>The Organiser must not provide false, misleading, incomplete, forged, altered, expired, or unauthorised documents or information. Providing false or misleading information is a material breach of these Terms and may result in:</P>
              <UL items={[
                "account rejection",
                "event suspension",
                "payout delay or refusal",
                "termination of services",
                "withholding of funds where permitted by law or payment rules",
                "reporting to banks, payment providers, regulators, authorities, or law enforcement where required or permitted",
                "recovery of losses, costs, chargebacks, refunds, penalties, and damages caused by the misrepresentation",
              ]} />
            </>
          ),
        },
        {
          id: "third-party",
          title: "11. Third-Party Verification Providers",
          body: (
            <>
              <P>Banana Cards may use third-party verification providers, payment providers, banks, fraud prevention services, sanctions screening tools, identity verification providers, document verification providers, and compliance partners to perform KYC, KYB, sanctions screening, fraud checks, payment risk checks, and related compliance functions.</P>
              <P>The Organiser agrees that Banana Cards may share necessary information and documents with such providers for verification, compliance, payment, risk management, and legal purposes, subject to Banana Cards' privacy policy and applicable data protection laws.</P>
            </>
          ),
        },
        {
          id: "data-retention",
          title: "12. Data Processing and Retention",
          body: (
            <>
              <P>Banana Cards may collect, process, verify, store, share, and retain KYC, KYB, beneficial ownership, tax, bank account, event, transaction, and compliance information for the purposes of:</P>
              <UL items={[
                "verifying the Organiser",
                "enabling ticket sales and payment collection",
                "enabling payouts",
                "preventing fraud, money laundering, terrorist financing, sanctions breaches, and other financial crime",
                "complying with legal, regulatory, tax, accounting, payment, and audit obligations",
                "resolving disputes, refunds, chargebacks, and claims",
                "enforcing these Terms",
              ]} />
              <P>Banana Cards may retain verification and compliance records for as long as reasonably necessary to comply with applicable law, accounting requirements, tax requirements, payment provider rules, dispute limitation periods, chargeback periods, regulatory requests, audit requirements, and legitimate business purposes.</P>
            </>
          ),
        },
        {
          id: "no-explain",
          title: "13. No Obligation to Explain Risk Decisions",
          body: (
            <>
              <P>Where permitted by law, Banana Cards is not required to disclose the detailed reasons, risk indicators, screening logic, internal rules, third-party scoring, or compliance findings behind any KYC, KYB, sanctions, fraud, payment risk, or payout decision.</P>
              <P>Banana Cards may provide general information where appropriate, but may withhold details where disclosure could compromise security, risk controls, legal compliance, fraud prevention, sanctions compliance, AML/CFT obligations, payment provider rules, or ongoing investigations.</P>
            </>
          ),
        },
        {
          id: "continuing",
          title: "14. Continuing Obligation",
          body: (
            <>
              <P>The Organiser's KYC and KYB obligations continue for as long as the Organiser uses Banana Cards, has active events, has pending payouts, has reserve balances, has unresolved refunds or chargebacks, or has continuing obligations under these Terms.</P>
              <P>The obligations in this section survive account closure, event completion, payout completion, suspension, or termination.</P>
            </>
          ),
        },
      ]}
    />
  );
}